import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import { getPaymentQueue, approvePayment, bulkApprovePayments } from '../../services/adminService';
import { formatCurrency, formatDate } from '../../utils/helpers';

const STATUS_TABS = [
  { key: 'held',      label: 'Pending Release',   color: 'text-accent'  },
  { key: 'cancelled', label: 'Cancellation Fees',  color: 'text-danger'  },
  { key: 'released',  label: 'Released',            color: 'text-success' },
];

function PaymentRow({ payment, selected, onSelect, onApprove, approving }) {
  const appt    = payment.appointmentId;
  const patient = payment.patientId;
  const doctor  = payment.doctorId;
  const isCancelled = payment.status === 'refunded';
  const canApprove  = payment.status === 'held' ||
    (isCancelled && payment.cancellationCharge > 0 && !payment.cancellationReleased);

  return (
    <div className="bg-white rounded-xl border border-border p-4 mb-2">
      <div className="flex items-start gap-3">
        {/* Checkbox — only for approvable */}
        {canApprove && (
          <input type="checkbox" checked={selected} onChange={() => onSelect(payment._id)}
            className="accent-primary w-4 h-4 mt-1 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <span className="text-sm font-bold text-text-primary">{patient?.name || '—'}</span>
              {isCancelled && (
                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                  <i className="fa-solid fa-ban mr-1 text-[8px]" />
                  Cancelled by {payment.cancelledBy || 'patient'}
                </span>
              )}
              <p className="text-xs text-text-muted">{patient?.phone}</p>
            </div>
            <div className="text-right">
              {isCancelled ? (
                <>
                  {payment.cancellationCharge > 0 ? (
                    <p className="text-sm font-bold text-text-primary">
                      ₹{payment.cancellationCharge} <span className="text-xs font-normal text-text-muted">cancel fee</span>
                    </p>
                  ) : (
                    <p className="text-xs text-text-muted">No cancel fee</p>
                  )}
                  <span className={`text-[10px] font-bold ${
                    payment.cancellationReleased ? 'text-success' : payment.cancellationCharge > 0 ? 'text-amber-600' : 'text-text-muted'
                  }`}>
                    {payment.cancellationReleased ? '✓ Fee Released to Doctor' :
                     payment.cancellationCharge > 0 ? '⏳ Pending Your Approval' : 'No Fee'}
                  </span>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold text-text-primary">{formatCurrency((payment.doctorFee || 0) - (payment.discountApplied || 0))}</p>
                  <span className={`text-[10px] font-bold ${payment.status === 'released' ? 'text-success' : 'text-accent'}`}>
                    {payment.status === 'released' ? '✓ Released' : '⏳ Pending'}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Appointment detail */}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
            <span><i className="fa-solid fa-user-doctor mr-1 text-primary" />{doctor?.name} · {doctor?.clinicName}</span>
            {appt?.appointmentDate && <span><i className="fa-solid fa-calendar mr-1" />{formatDate(appt.appointmentDate)}</span>}
            {appt?.tokenNumber && <span><i className="fa-solid fa-hashtag mr-1" />Token {appt.tokenNumber}</span>}
            {appt?.bookingId && <span className="font-mono text-[10px]">{appt.bookingId}</span>}
          </div>

          {/* Cancellation breakdown */}
          {isCancelled && (
            <div className="mt-2 p-2.5 bg-red-50 border border-red-100 rounded-lg text-xs space-y-0.5">
              <p className="font-semibold text-red-700 mb-1">Cancellation Breakdown</p>
              <div className="flex justify-between"><span className="text-text-muted">Total Paid</span><span>₹{payment.totalAmount}</span></div>
              <div className="flex justify-between"><span className="text-text-muted">Patient Refund</span><span className="text-info">₹{payment.patientRefund || payment.refundAmount || 0}</span></div>
              <div className="flex justify-between"><span className="text-text-muted">Platform (Fees)</span><span>₹{payment.adminShare || 0}</span></div>
              <div className="flex justify-between border-t border-red-200 pt-1 mt-1">
                <span className="font-semibold text-red-700">Doctor Cancel Fee</span>
                <span className="font-bold text-red-700">₹{payment.cancellationCharge || 0}</span>
              </div>
            </div>
          )}

          {/* Normal payment breakdown */}
          {!isCancelled && payment.discountApplied > 0 && (
            <p className="text-xs text-success mt-1">
              <i className="fa-solid fa-tag mr-1" />₹{payment.discountApplied} discount applied
            </p>
          )}
        </div>

        {/* Action button */}
        {canApprove && (
          <button onClick={() => onApprove(payment._id)} disabled={approving === payment._id}
            className="flex-shrink-0 px-3 py-2 bg-success text-white text-xs font-bold rounded-xl flex items-center gap-1.5">
            {approving === payment._id
              ? <i className="fa-solid fa-spinner animate-spin" />
              : <i className="fa-solid fa-check" />}
            {isCancelled ? 'Release Fee' : 'Approve'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [activeTab, setActiveTab]   = useState('held');
  const [payments,  setPayments]    = useState([]);
  const [total,     setTotal]       = useState(0);
  const [loading,   setLoading]     = useState(false);
  const [approving, setApproving]   = useState(null);
  const [selected,  setSelected]    = useState([]);
  const [page,      setPage]        = useState(1);
  const [pendingCancelCount, setPendingCancelCount] = useState(0);
  const [fromDate,  setFromDate]    = useState('');
  const [toDate,    setToDate]      = useState('');

  const load = useCallback(async (tab, pg, from, to) => {
    setLoading(true);
    try {
      const res = await getPaymentQueue({ status: tab, page: pg, limit: 20, from, to });
      setPayments(pg === 1 ? (res.payments || []) : prev => [...prev, ...(res.payments || [])]);
      setTotal(res.total || 0);
      if (res.pendingCancelCount !== undefined) setPendingCancelCount(res.pendingCancelCount);
    } catch (err) {
      toast.error(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPayments([]); setSelected([]); setPage(1);
    load(activeTab, 1, fromDate, toDate);
  }, [activeTab, load, fromDate, toDate]);

  const handleApprove = async (id) => {
    setApproving(id);
    try {
      await approvePayment(id);
      toast.success(activeTab === 'cancelled' ? 'Cancellation fee released to doctor!' : 'Payment released to doctor!');
      load(activeTab, 1, fromDate, toDate);
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setApproving(null); setSelected([]);
    }
  };

  const handleBulkApprove = async () => {
    if (!selected.length) { toast.error('Select payments to approve'); return; }
    try {
      await bulkApprovePayments(selected);
      toast.success(`${selected.length} payment(s) approved!`);
      setSelected([]); load(activeTab, 1, fromDate, toDate);
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const loadMore = () => { const next = page + 1; setPage(next); load(activeTab, next, fromDate, toDate); };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-black text-text-primary font-heading">Payment Queue</h1>
            <p className="text-sm text-text-muted">Approve payments & cancellation fees for doctors</p>
          </div>
          {pendingCancelCount > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-2 rounded-xl">
              <i className="fa-solid fa-triangle-exclamation text-red-500" />
              <p className="text-xs font-bold text-red-700">{pendingCancelCount} cancellation fee{pendingCancelCount !== 1 ? 's' : ''} need approval</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface rounded-xl p-1 mb-4 border border-border">
          {STATUS_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                activeTab === tab.key ? 'bg-white shadow-sm text-text-primary' : 'text-text-muted'
              }`}>
              <span className={activeTab === tab.key ? tab.color : ''}>{tab.label}</span>
              {tab.key === 'cancelled' && pendingCancelCount > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-black px-1 rounded-full">{pendingCancelCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Date filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-1">
              <label className="text-[10px] text-text-muted font-semibold uppercase tracking-wide block mb-1">From</label>
              <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }}
                className="input text-sm py-2" />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-text-muted font-semibold uppercase tracking-wide block mb-1">To</label>
              <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }}
                className="input text-sm py-2" />
            </div>
          </div>
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(''); setToDate(''); }}
              className="mt-5 px-3 py-2 text-xs text-danger font-semibold border border-red-200 bg-red-50 rounded-xl">
              <i className="fa-solid fa-xmark mr-1" />Clear
            </button>
          )}
        </div>

        {/* Bulk approve bar */}
        {selected.length > 0 && activeTab === 'held' && (
          <div className="mb-3 flex items-center justify-between bg-primary/10 border border-primary/20 rounded-xl px-4 py-2.5">
            <p className="text-sm font-semibold text-primary">{selected.length} selected</p>
            <button onClick={handleBulkApprove}
              className="px-4 py-2 bg-success text-white text-xs font-bold rounded-xl">
              <i className="fa-solid fa-check-double mr-1.5" />Approve All Selected
            </button>
          </div>
        )}

        {/* List */}
        {loading && payments.length === 0 ? (
          <InlineLoader text="Loading payments..." />
        ) : payments.length === 0 ? (
          <div className="text-center py-16">
            <i className="fa-solid fa-check-circle text-4xl text-success mb-3 block" />
            <p className="text-text-muted text-sm">
              {activeTab === 'cancelled' ? 'No cancellation fees pending approval' : 'No payments in this queue'}
            </p>
          </div>
        ) : (
          <>
            <div>
              {payments.map(p => (
                <PaymentRow key={p._id} payment={p}
                  selected={selected.includes(p._id)}
                  onSelect={toggleSelect}
                  onApprove={handleApprove}
                  approving={approving} />
              ))}
            </div>
            {payments.length < total && (
              <button onClick={loadMore}
                className="w-full py-3 text-sm text-primary font-semibold border border-primary rounded-xl mt-2">
                Load More
              </button>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
